import { QUERY_PARAM_ENUM } from "@/constants/enums";
import { MAX_HASHED_TIMES } from "@/constants/layout";
import { useAppSelector } from "@/store";
import { isWebView } from "@/utils/webview";

/**
 * `useHashQueryId` — the original's module 68910, and the reason the browser's
 * back button closes an overlay screen instead of leaving the site.
 *
 * Moving between screens inside a drawer is not a route change, so the history
 * stack would normally know nothing about it. Each screen change therefore
 * pushes a synthetic history entry; a global `popstate` listener (see
 * `BackButtonBridge`) pops it and clicks the topmost back control instead of
 * navigating.
 *
 * Two guards, both the original's:
 *
 * - Inside the RN shell it is a no-op — the native side owns the back gesture.
 * - It stops after three pushes (`HASHED_TIMES`), so a customer wandering
 *   through a deep stack cannot build a history they have to press back through
 *   a dozen times to escape.
 *
 * The pushed URL carries the current `order_type`, so a shared or reloaded link
 * lands the customer back on the same ordering mode.
 */

/** Module-level, matching the original's `GLOBAL.HASHED_TIMES` counter. */
const GLOBAL = { HASHED_TIMES: 0 };

export const decrementHashedTimes = (): void => {
  GLOBAL.HASHED_TIMES -= 1;
};

export default function useHashQueryId(): { setNewHashQueryId: () => void } {
  // The original reads `cart.data.platform` (`WEB_DELIVERY`) and strips the
  // `WEB_`/`APP_` prefix. This tree keeps the order type on its own, which is
  // the same value with the prefix already gone.
  const orderType = useAppSelector((state) => state.cart.orderType);

  return {
    setNewHashQueryId: () => {
      if (isWebView() || GLOBAL.HASHED_TIMES >= MAX_HASHED_TIMES) return;

      let url: string | null = null;
      if (orderType != null) {
        const params = new URLSearchParams(window.location.search);
        params.set(QUERY_PARAM_ENUM.orderType, orderType.toLowerCase());
        url = `${window.location.pathname}?${params.toString()}`;
      }

      // A null url pushes the current one, which is exactly what the original
      // does when the cart has no platform yet: the entry still exists, so back
      // still has something to pop.
      window.history.pushState({ request_id: crypto.randomUUID() }, "", url);
      GLOBAL.HASHED_TIMES += 1;
    },
  };
}
